import { reactive, toRaw, watch } from 'vue';

import noop from 'lodash/noop';
// PRODUCT-26110 Phase 2B2: 'vue-router/types/router' is a vue-router-3-only subpath,
// not present in v4's `exports` map at all -- module-not-found, not just a type
// mismatch. `VueRouter` -> `Router`.
import type { Router } from 'vue-router';

import type { Pinia } from 'pinia';

import { useUserStore } from './store/user';
import { RelyingParty } from './types';

interface RpControllerApp {
    router: Router;
    getPinia(): Pinia;
}

class RpController {
    private app: RpControllerApp;
    public current!: string | null;
    public available: RelyingParty[] = [];
    public currentDetails: RelyingParty | null = null;

    public constructor(app: RpControllerApp) {
        this.app = app;
    }

    // PRODUCT-26110 Phase 2B2: called by createRpController() on the reactive proxy
    // (NOT from the constructor, which runs on the raw instance before `reactive()`
    // wraps it). The subscription callbacks must capture the PROXY as `this`, or every
    // later `update()` writes to the raw target and never triggers reactivity -- the
    // RP selector then displays a stale RP forever.
    public init(): void {
        this.update();
        this.subscribe();
    }

    // PRODUCT-26110 Phase 2B2 note on the `toRaw(this.app)` locals below:
    // `reactive()` deep-wraps `this.app` -- and a reactive proxy auto-UNWRAPS refs
    // on property access, so
    // `this.app.router.currentRoute` returned the route object instead of the Ref and
    // `.value` came back undefined (crashing `change()`). The unwrap must be a
    // METHOD-LOCAL `toRaw()`: the proxy's `get` trap re-wraps any object a property
    // access returns, so even a getter that returns `toRaw(this.app)` hands back a
    // reactive proxy again.

    private update(): void {
        const app = toRaw(this.app);
        this.available = useUserStore(app.getPinia()).relyingParties || [];
        // PRODUCT-26110 Phase 2B2: router.currentRoute is a Ref under vue-router 4.
        // vue-router 4 types repeated params as `string | string[]`, stricter than
        // vue-router 3 -- this route has no repeated `rpId` segment, so `as string`
        // matches ed2202df8's established narrowing (see b2-findings.md).
        this.current = (app.router.currentRoute.value?.params.rpId as string | undefined) || null;
        this.currentDetails = this.available.find((rp) => rp.name === this.current) || null;
    }

    private subscribe(): void {
        const app = toRaw(this.app);
        const userStore = useUserStore(app.getPinia());
        watch(() => userStore.relyingParties, () => this.update());
        app.router.afterEach(() => this.update());
    }

    public async change(rpId: string): Promise<void> {
        const app = toRaw(this.app);
        // PRODUCT-26110 Phase 2B2: router.currentRoute is a Ref under vue-router 4.
        const route = app.router.currentRoute.value;
        // vue-router 4 types route names as `RouteRecordName = string | symbol`,
        // stricter than vue-router 3's bare `string` -- coerce at this narrow call
        // site, matching ed2202df8's established pattern.
        const internal = /^auth\./.test(String(route.name || ''));
        const nextUrl = typeof route.query.next === 'string'
            ? route.query.next
            : null;
        if (internal && nextUrl) {
            // PRODUCT-26110 Phase 2B2: vue-router 3's router.resolve() wrapped its
            // result in { route, location, href } -- vue-router 4's resolve() returns
            // the resolved route directly, and always returns a value (never undefined).
            const resolvedRoute = app.router.resolve(nextUrl);
            if (resolvedRoute) {
                await app.router.push({
                    name: resolvedRoute.name,
                    params: { ...resolvedRoute.params, rpId },
                    query: resolvedRoute.query,
                } as any).catch(noop);
                return;
            }
        }
        // PRODUCT-26110 Phase 2B2: do NOT spread the whole normalized route into push().
        // The spread includes `path` (with the OLD rpId baked in), and vue-router 4
        // resolves `path` with priority, silently IGNORING `params` -- so switching RP
        // pushed the current path and deduped to a no-op (vue-router 3 gave `name`
        // precedence, which is why the spread used to work). Push an explicit
        // name-based location instead so the new rpId param takes effect.
        await app.router.push({
            name: route.name,
            params: { ...route.params, rpId },
            query: { ...route.query, next: undefined },
            hash: route.hash,
        } as any).catch(noop);
    }
}

export function createRpController(app: RpControllerApp): RpController {
    // PRODUCT-26110 Task 5: `reactive()` replaces `@vue/compat`'s `Vue.observable`
    // (GLOBAL_OBSERVABLE migrated off compat). `reactive()`'s declared return type
    // (`UnwrapNestedRefs<T>`) is a fresh mapped-object type, not `RpController` itself,
    // so it structurally drops the class's private members (TS2739) even though the
    // reactive proxy wraps the exact same instance at runtime -- cast back to the
    // real class type.
    const controller = reactive(new RpController(app)) as RpController;
    // Initialize through the reactive proxy so subscription callbacks capture the
    // proxy as `this` and their writes trigger reactivity (see RpController.init).
    controller.init();
    return controller;
}
